import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Select, { components as selectComponents } from "react-select"
import debounce from "lodash/debounce"
import { get } from "../../api/axios"
import endpoints from "../../api/endpoints"
import {
  customStyles,
  ChevronDropdown,
} from "../OnboardingComponents/RoleSelect"

const DEBOUNCE_MS = 300
/** Match Explore Cities modal page size. */
const CITY_PAGE_SIZE = 10

const formatCityOptionLabel = city => {
  if (!city) return ""
  const name = city.city || city.name || ""
  const state = city.state || ""
  if (name && state) return `${name}, ${state}`
  return name || String(city.id || "")
}

const mapCitiesToOptions = (list = []) =>
  list
    .map(city => ({
      value: city?.id,
      label: formatCityOptionLabel(city),
      raw: city,
    }))
    .filter(option => option.value && option.label)

const mergeOptions = (prev, next) => {
  const seen = new Set(prev.map(option => String(option.value)))
  return [
    ...prev,
    ...next.filter(option => !seen.has(String(option.value))),
  ]
}

/**
 * Preferred city dropdown.
 * - Static: pass `cityOptions` (e.g. user’s 1–3 address cities).
 * - Paginated API: set `loadFromApi` — search + infinite scroll on menu
 *   (same page size / scroll pattern as Explore Cities + RoleSelect).
 * - `variant="auth"`: Invite / Create Account — uses auth `select2-selection` chrome.
 */
const PreferredCitySelect = ({
  cityOptions = [],
  loadFromApi = false,
  excludeIds = [],
  extraOptions = [],
  value: valueProp,
  valueLabel = "",
  onChange: onChangeProp,
  error: errorProp,
  onBlur: onBlurProp,
  validation,
  label = "Preferred City",
  labelClassName = "",
  placeholder = "Select a city",
  className = "",
  isDisabled = false,
  required = false,
  variant = "default",
  menuPortalTarget = typeof document !== "undefined" ? document.body : null,
}) => {
  const isAuth = variant === "auth"
  const value = valueProp ?? validation?.values?.preferredCity
  const error = errorProp ?? validation?.errors?.preferredCity
  const setFieldValue = validation?.setFieldValue
  const setFieldTouched = validation?.setFieldTouched

  const [inputValue, setInputValue] = useState("")
  const [searchQuery, setSearchQuery] = useState("")
  const [apiOptions, setApiOptions] = useState([])
  const [nextPageUrl, setNextPageUrl] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  const latestRequest = useRef(0)
  const abortRef = useRef(null)

  const resolvedMenuPortalTarget = isAuth ? null : menuPortalTarget

  const excluded = useMemo(
    () =>
      new Set(
        (Array.isArray(excludeIds) ? excludeIds : [])
          .filter(Boolean)
          .map(id => String(id)),
      ),
    [excludeIds],
  )

  const extras = useMemo(
    () => (Array.isArray(extraOptions) ? extraOptions : []).filter(Boolean),
    [extraOptions],
  )

  const filterExcluded = useCallback(
    options =>
      (Array.isArray(options) ? options : []).filter(
        option => !excluded.has(String(option?.value)),
      ),
    [excluded],
  )

  const staticOptions = useMemo(() => {
    if (loadFromApi) return []
    return filterExcluded([
      ...(Array.isArray(cityOptions) ? cityOptions : []),
      ...extras,
    ])
  }, [loadFromApi, filterExcluded, extras, cityOptions])

  const menuOptions = useMemo(() => {
    if (!loadFromApi) return staticOptions
    return filterExcluded([...apiOptions, ...extras])
  }, [loadFromApi, staticOptions, filterExcluded, extras, apiOptions])

  const selected = useMemo(() => {
    if (value == null || value === "") return null
    const fromMenu = menuOptions.find(
      option => String(option.value) === String(value),
    )
    if (fromMenu) return fromMenu
    if (valueLabel) return { value, label: valueLabel }
    const fromStatic = (cityOptions || []).find(
      option => String(option.value) === String(value),
    )
    if (fromStatic) return fromStatic
    return { value, label: String(valueLabel || value) }
  }, [value, valueLabel, menuOptions, cityOptions])

  const selectStyles = isAuth ? undefined : customStyles(!!error)

  const debouncedSetQuery = useMemo(
    () => debounce(q => setSearchQuery(q), DEBOUNCE_MS),
    [],
  )

  const load = useCallback(
    async ({ query = "", pageUrl = null, isLoadMore = false } = {}) => {
      if (!loadFromApi) return

      const requestId = ++latestRequest.current
      if (abortRef.current) abortRef.current.abort()
      const controller = new AbortController()
      abortRef.current = controller

      if (isLoadMore) {
        setIsLoading(false)
        setIsLoadingMore(true)
      } else {
        setIsLoadingMore(false)
        setIsLoading(true)
      }

      try {
        const response = pageUrl
          ? await get(pageUrl, { signal: controller.signal, skipErrorToast: true })
          : await get(endpoints.getCites, {
              signal: controller.signal,
              skipErrorToast: true,
              params: {
                search: query || "",
                page: 1,
                page_size: CITY_PAGE_SIZE,
                per_page: CITY_PAGE_SIZE,
                sort_direction: "asc",
              },
            })

        if (requestId !== latestRequest.current) return

        const result = response?.data?.result
        const list = Array.isArray(result)
          ? result
          : Array.isArray(result?.data)
            ? result.data
            : []
        const next = Array.isArray(result)
          ? response?.data?.next_page_url
          : result?.next_page_url
        const mapped = mapCitiesToOptions(list)

        setApiOptions(prev =>
          isLoadMore ? mergeOptions(prev, mapped) : mapped,
        )
        setNextPageUrl(next || null)
      } catch {
        // Aborted / failed — keep prior options; empty/loading UI handles UX.
      } finally {
        if (requestId !== latestRequest.current) return
        if (isLoadMore) setIsLoadingMore(false)
        else setIsLoading(false)
      }
    },
    [loadFromApi],
  )

  useEffect(() => {
    if (!loadFromApi || !menuOpen) return undefined
    load({ query: searchQuery })
    return undefined
  }, [loadFromApi, menuOpen, searchQuery, load])

  useEffect(
    () => () => {
      if (abortRef.current) abortRef.current.abort()
      debouncedSetQuery.cancel()
    },
    [debouncedSetQuery],
  )

  const handleInputChange = useCallback(
    (newValue, { action }) => {
      if (!loadFromApi) return
      if (action !== "input-change") return
      setInputValue(newValue)
      debouncedSetQuery(newValue)
    },
    [loadFromApi, debouncedSetQuery],
  )

  const handleChange = useCallback(
    option => {
      setInputValue("")
      setSearchQuery("")
      const nextValue = option ? option.value : ""
      if (onChangeProp) {
        onChangeProp(nextValue, option || null)
      } else {
        setFieldValue?.("preferredCity", nextValue)
      }
      setMenuOpen(false)
    },
    [onChangeProp, setFieldValue],
  )

  const handleMenuOpen = useCallback(() => {
    if (isDisabled) return
    setMenuOpen(true)
    if (loadFromApi) {
      setSearchQuery(inputValue)
      debouncedSetQuery.cancel()
    }
  }, [isDisabled, loadFromApi, inputValue, debouncedSetQuery])

  const handleMenuClose = useCallback(() => {
    setMenuOpen(false)
    if (onBlurProp) {
      onBlurProp()
    } else {
      setFieldTouched?.("preferredCity", true)
    }
  }, [onBlurProp, setFieldTouched])

  const handleBlur = useCallback(() => {
    if (onBlurProp) {
      onBlurProp()
    } else {
      setFieldTouched?.("preferredCity", true)
    }
  }, [onBlurProp, setFieldTouched])

  const handleScrollToBottom = useCallback(() => {
    if (!loadFromApi) return
    if (isLoading || isLoadingMore || !nextPageUrl) return
    load({ query: searchQuery, pageUrl: nextPageUrl, isLoadMore: true })
  }, [
    loadFromApi,
    isLoading,
    isLoadingMore,
    nextPageUrl,
    load,
    searchQuery,
  ])

  const MenuList = useCallback(
    props => (
      <selectComponents.MenuList {...props}>
        {props.children}
        {props.selectProps?.isLoadingMore ? (
          <div
            className={
              isAuth
                ? "amok-skeleton preferred-city-select__loading-more"
                : "amok-skeleton amok-role-select__loading-more"
            }
            aria-hidden="true"
          />
        ) : null}
      </selectComponents.MenuList>
    ),
    [isAuth],
  )

  const selectClassName = isAuth
    ? `${error ? "is-invalid" : ""} ${className}`.trim()
    : `amok-role-select ${error ? "amok-role-select--error" : ""}`

  const selectNode = (
    <Select
      className={selectClassName}
      classNamePrefix={isAuth ? "select2-selection" : "amok-role-select"}
      options={menuOptions}
      value={selected}
      onChange={handleChange}
      onBlur={loadFromApi ? undefined : handleBlur}
      inputValue={loadFromApi ? inputValue : undefined}
      onInputChange={loadFromApi ? handleInputChange : undefined}
      placeholder={placeholder}
      isSearchable
      isClearable={false}
      isDisabled={isDisabled}
      isLoading={loadFromApi ? isLoading && menuOptions.length === 0 : false}
      isLoadingMore={isLoadingMore}
      maxMenuHeight={isAuth ? 180 : undefined}
      menuPlacement={isAuth ? "auto" : undefined}
      openMenuOnFocus={!isDisabled}
      openMenuOnClick={!isDisabled}
      menuIsOpen={loadFromApi ? menuOpen : undefined}
      onMenuOpen={loadFromApi ? handleMenuOpen : undefined}
      onMenuClose={loadFromApi ? handleMenuClose : undefined}
      onMenuScrollToBottom={loadFromApi ? handleScrollToBottom : undefined}
      menuPortalTarget={resolvedMenuPortalTarget}
      menuPosition={resolvedMenuPortalTarget ? "fixed" : undefined}
      components={{
        ...(isAuth
          ? {}
          : {
              DropdownIndicator: ChevronDropdown,
              IndicatorSeparator: null,
            }),
        ...(loadFromApi ? { MenuList } : {}),
        ...(isAuth ? { IndicatorSeparator: null } : {}),
      }}
      noOptionsMessage={() => "No cities found"}
      loadingMessage={() => "Loading..."}
      styles={
        isAuth
          ? undefined
          : {
              ...selectStyles,
              menuPortal: base => ({ ...base, zIndex: 10060 }),
            }
      }
    />
  )

  if (isAuth) {
    const authLabelClass =
      labelClassName ||
      `form-label${required ? " isRequired" : ""}`.trim()
    return (
      <>
        {label ? <label className={authLabelClass}>{label}</label> : null}
        {selectNode}
        {error ? (
          <div className="invalid-feedback d-block">{error}</div>
        ) : null}
      </>
    )
  }

  return (
    <div
      className={`amok-field ${error ? "amok-field--error" : ""} ${className}`.trim()}
    >
      {label && (
        <label className="amok-field__label">
          {label}
          {required ? <span className="amok-field__required"> *</span> : null}
        </label>
      )}
      {selectNode}
      {error && (
        <p className="amok-field__message amok-field__message--error">
          {error}
        </p>
      )}
    </div>
  )
}

export default PreferredCitySelect
